import { DateRangePickers } from '@/components/common/DateRangePickers';
import TableSearchInput from '@/components/common/TableSearchInput';
import PharmacistBatchOrdersTable from '@/components/orders/PharmacistBatchOrderTable';
import {
  ApprovalsTableCell,
  ArrayValueTableCell,
  DoctorNoteTableCell,
  OrderSelectTableCell,
  PrescriptionsTableCell,
  ProductNameTableCell,
  ShipmentStatusTableCell,
} from '@/components/orders/tables/cells/PharmacistOrdersTableCell';
import { ActionsContainer, ActionsRow } from '@/components/orders/tables/filters/Actions';
import DownloadOrdersButton from '@/components/orders/tables/filters/DownloadOrdersButton';
import {
  isWithinOneMonth,
  sortDispenseDate,
  sortFieldOverrides,
} from '@/components/orders/utils/PharmacistOrdersTableUtils';
import settings from '@/constants/constants';
import {
  FF_ENABLE_BATCH_DISPENSE_VIEW,
  FF_ENABLE_DISPENSING_LIMIT_COLUMNS_CHANGES,
  FF_ENABLE_PHARMACY_ORDER_PAYMENT_RECEIPTS_BULK_DOWNLOAD,
  FF_ENABLE_PROCESSING_DISPENSE_NOTE,
  FF_PHARMACIST_ORDER_LISTS_DATE_RANGE_FILTER,
} from '@/constants/featureFlags';
import { PharmacistOrderTableView, usePharmacistOrders } from '@/context/orders/PharmacistOrders';
import { useGetOrderReceipt } from '@/hooks/orders/useGetOrderReceipt';
import { useOrdersPageSizeOptions } from '@/hooks/orders/useOrdersPageSizeOptions';
import { useOrdersSearch } from '@/hooks/orders/useOrdersSearch';
import useTableCountWithLoading from '@/hooks/table/useTableCountWithLoading';
import { useTableManualPagination } from '@/hooks/table/useTableManualPagination';
import useTableSortingOrder from '@/hooks/table/useTableSortingOrder';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useGetPharmacistOrders from '@/hooks/useGetPharmacistOrders';
import { formatISODateHelper } from '@/utils/helpers';
import { dateColumnSort } from '@/utils/table';
import { useAsyncFileGeneration, useClearSelectedOrders, useSelectedOrders } from '@montugroup/circuit-shared';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import type { PharmacistOrder } from '@montugroup/circuit-shared/types/pharmacist';
import type { PaginationModel } from '@montugroup/design-system';
import { Button, CircularProgress, PaginationVariant, Table, toast } from '@montugroup/design-system';
import Download from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Stack } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';

function DispensedOrdersTable() {
  const { flags } = useFeatureFlags();
  const {
    paginationModel: { page, pageSize },
    setPaginationModel,
    shouldResetPageIndex,
    setShouldResetPageIndex,
  } = useTableManualPagination({
    page: 0,
    pageSize: 50,
  });
  const { sortingOrder, handleSortingOrderChange } = useTableSortingOrder({ sortFieldOverrides });
  const { search, handleSearch, clearSearch } = useOrdersSearch();
  const pageSizeOptions = useOrdersPageSizeOptions();
  const { requestFile } = useAsyncFileGeneration(getTsApiV1Client().pharmacy.createBulkOrderReceiptDocument);

  const selectedOrders = useSelectedOrders();
  const clearSelectedOrders = useClearSelectedOrders();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [downloadReceiptOrderId, setDownloadReceiptOrderId] = useState<number | null>(null);

  const { data: receiptData } = useGetOrderReceipt({ orderId: downloadReceiptOrderId });
  const { batchView, setBatchView } = usePharmacistOrders();
  const { data, isLoading } = useGetPharmacistOrders({
    page,
    pageSize,
    sortingOrder,
    filter: search,
    activeTab: PharmacistOrderTableView.DISPENSED_ORDERS,
    statusFilter: [],
    isEnabled: true,
    startDate: startDate,
    endDate: endDate,
  });

  const count = useTableCountWithLoading(isLoading, data?.count);

  const handleBulkReceiptDownload = async () => {
    if (selectedOrders.length === 0 && count === 0) {
      toast.error('Filters must return orders before proceeding with action.');
      return;
    }

    if (!startDate || !isWithinOneMonth({ startDate, endDate: endDate || new Date() })) {
      toast.error('Outside date range limit. Maximum date range is 1 month.');
      return;
    }

    if (selectedOrders.length !== 0) {
      requestFile(
        { data: { orderIds: selectedOrders } },
        {
          fileName: 'RECEIPTS.zip',
        },
      );
    } else {
      const startDateLuxon = DateTime.fromJSDate(startDate);
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const endDateLuxon = DateTime.fromJSDate(endDate || new Date());

      // Example fileName: RECEIPTS_06-JAN-2025_TO_05-FEB-2025.zip
      const fileName = `RECEIPTS_${startDateLuxon.toFormat('dd-LLL-yyyy').toUpperCase()}_TO_${endDateLuxon.toFormat('dd-LLL-yyyy').toUpperCase()}.zip`;

      requestFile(
        {
          data: {
            filter: search,
            statusIds: [settings.orderStatus.PHARMACY_DISPENSED, settings.orderStatus.PHARMACY_COMPLETED],
            startDate,
            endDate: endDateLuxon.endOf('day').toJSDate(),
          },
        },
        {
          fileName,
        },
      );
    }
  };

  const handlePaginationModelChange = (model: PaginationModel) => {
    clearSelectedOrders();
    setPaginationModel(model);
    setShouldResetPageIndex(false);
  };

  useEffect(() => {
    if (receiptData?.receiptUrl) {
      window.open(receiptData.receiptUrl);
      setDownloadReceiptOrderId(null);
    }
  }, [receiptData]);

  const columns: ColumnDef<PharmacistOrder>[] = useMemo(
    () => [
      ...(!flags[FF_ENABLE_DISPENSING_LIMIT_COLUMNS_CHANGES]
        ? ([
            {
              accessorKey: 'shipped_date',
              header: 'Date shipped',
              enableSorting: true,
              accessorFn: (originalRow) =>
                originalRow.shipped_date ? formatISODateHelper(originalRow.shipped_date) : '-',
              sortingFn: (rowA, rowB) => dateColumnSort(rowA.original.shipped_date, rowB.original.shipped_date),
            },
          ] as ColumnDef<PharmacistOrder>[])
        : []),
      {
        accessorKey: 'order_code',
        header: 'Order ID',
        enableSorting: true,
      },
      {
        accessorKey: 'order_date',
        header: 'Order placed',
        enableSorting: true,
        accessorFn: (originalRow) => formatISODateHelper(originalRow.order_date),
        sortingFn: (rowA, rowB) => dateColumnSort(rowA.original.order_date, rowB.original.order_date),
      },
      {
        accessorKey: 'patient_id',
        header: 'Patient ID',
        enableSorting: true,
      },
      {
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
      },
      {
        accessorKey: 'approvals',
        header: 'Approvals',
        enableSorting: false,
        cell: ({ row }) => <ApprovalsTableCell patientCode={row.original.patient_id} orderId={row.original.orderId} />,
      },
      {
        accessorKey: 'prescriptions',
        header: 'Prescriptions',
        enableSorting: false,
        cell: ({ row }) => (
          <PrescriptionsTableCell prescriptions={row.original.prescriptions} fallbackGPName={row.original.gp_name} />
        ),
      },
      {
        accessorKey: 'product_name',
        header: 'Product',
        enableSorting: false,
        cell: ({ row }) => <ProductNameTableCell productNames={row.original.product_details.map(({ name }) => name)} />,
      },
      ...(!flags[FF_ENABLE_DISPENSING_LIMIT_COLUMNS_CHANGES]
        ? ([
            {
              accessorKey: 'quantity',
              header: 'Quantity',
              enableSorting: false,
              cell: ({ row }) => <ArrayValueTableCell values={row.original.quantity} />,
            },
          ] as ColumnDef<PharmacistOrder>[])
        : []),
      {
        accessorKey: 'repeats',
        header: 'Repeats',
        enableSorting: false,
        cell: ({ row }) => <ArrayValueTableCell values={row.original.repeats} />,
      },
      {
        accessorKey: 'dispensed_date',
        header: 'Dispensed date',
        enableSorting: true,
        accessorFn: (originalRow) =>
          originalRow.dispensed_date ? formatISODateHelper(originalRow.dispensed_date) : '-',
        sortingFn: (rowA, rowB) => sortDispenseDate(rowA.original.dispensed_date, rowB.original.dispensed_date),
      },
      {
        accessorKey: 'shipmentStatus',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <ShipmentStatusTableCell status={row.original.shipmentStatus} orderId={row.original.orderId} />
        ),
      },
      ...(flags[FF_ENABLE_PROCESSING_DISPENSE_NOTE]
        ? ([
            {
              accessorKey: '',
              id: 'doctorNote',
              header: 'Doctor Note',
              enableSorting: false,
              cell: ({ row }) => <DoctorNoteTableCell prescriptions={row.original.prescriptions} />,
            },
          ] as ColumnDef<PharmacistOrder>[])
        : []),
      {
        accessorKey: 'receipt',
        header: 'Payment receipt',
        cell: ({ row }) => {
          if (downloadReceiptOrderId && row.original.orderId === downloadReceiptOrderId) {
            return <CircularProgress size={24} />;
          }

          const isDisabled = !row.original.receiptAvailable;

          return (
            <PictureAsPdfIcon
              aria-label="download payment receipt"
              aria-disabled={isDisabled}
              color={isDisabled ? 'disabled' : 'primary'}
              cursor={isDisabled ? 'auto' : 'pointer'}
              pointerEvents={isDisabled ? 'none' : 'auto'}
              onClick={() => {
                if (isDisabled) {
                  return;
                }

                setDownloadReceiptOrderId(row.original.orderId);
              }}
            />
          );
        },
      },
      {
        accessorKey: 'select',
        header: 'Select',
        enableSorting: false,
        cell: ({ row }) => <OrderSelectTableCell orderId={row.original.id} />,
      },
    ],
    [downloadReceiptOrderId, flags],
  );

  const clearAllFilters = () => {
    clearSelectedOrders();
    clearSearch();
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <Stack direction={'column'} gap={4}>
      <Stack direction={'column'} gap={4}>
        <Stack direction={{ sm: 'column', md: 'row' }} gap={4} justifyContent={'space-between'}>
          <TableSearchInput value={search} onChange={handleSearch} />
          <Stack direction={{ sm: 'column', md: 'row' }} gap={4} alignItems="center">
            <DownloadOrdersButton
              selectedOrders={selectedOrders}
              count={count}
              isLoading={isLoading}
              startDate={startDate}
              endDate={endDate}
              search={search}
              statusFilter={[settings.orderStatus.PHARMACY_DISPENSED, settings.orderStatus.PHARMACY_COMPLETED]}
            />
            {flags[FF_ENABLE_PHARMACY_ORDER_PAYMENT_RECEIPTS_BULK_DOWNLOAD] && (
              <Button
                variant="contained"
                size="large"
                onClick={handleBulkReceiptDownload}
                disabled={isLoading}
                startIcon={<Download />}
              >
                Download bulk Receipts
              </Button>
            )}
          </Stack>
        </Stack>
        <Stack
          direction={{ sm: 'column', md: 'row' }}
          gap={4}
          justifyContent={'space-between'}
          width={'100%'}
          minWidth={'fit-content'}
        >
          {flags[FF_PHARMACIST_ORDER_LISTS_DATE_RANGE_FILTER] && (
            <Stack direction={{ sm: 'column', md: 'row' }} gap={4}>
              <DateRangePickers
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
              <Button
                variant="outlined"
                size="large"
                disabled={!startDate && !endDate && !search && selectedOrders.length === 0}
                sx={{ alignSelf: 'stretch' }}
                onClick={clearAllFilters}
              >
                Clear all filters
              </Button>
            </Stack>
          )}
          <ActionsContainer>
            {flags[FF_ENABLE_BATCH_DISPENSE_VIEW] && (
              <ActionsRow>
                <Button variant="contained" size="large" onClick={() => setBatchView(!batchView)} disabled={isLoading}>
                  {batchView ? 'Order View' : 'Batch View'}
                </Button>
              </ActionsRow>
            )}
          </ActionsContainer>
        </Stack>
      </Stack>
      {batchView ? (
        <PharmacistBatchOrdersTable />
      ) : (
        <Table
          data={data?.orders ?? []}
          columns={columns}
          showPagination
          manualPagination
          shouldResetPageIndex={shouldResetPageIndex}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          total={count}
          paginationVariant={PaginationVariant.VARIABLE_PAGE_SIZE}
          onPaginationModelChange={handlePaginationModelChange}
          onSortingChange={handleSortingOrderChange}
          isLoading={isLoading}
        />
      )}
    </Stack>
  );
}

export default DispensedOrdersTable;
